import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import VolymFactBox from "@/components/VolymFactBox";
import { useLanguage } from "@/contexts/LanguageContext";
import { Leaf, Zap, Shield, Eye, Star, Target, Globe, TrendingUp, Award, Rocket } from "lucide-react";
const OmOss = () => {
  const {
    language,
    setLanguage,
    content: currentContent
  } = useLanguage();
  return <div className="min-h-screen bg-background">
      <Header language={language} onLanguageChange={setLanguage} content={currentContent.header} />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-2 md:pt-20 md:pb-4 overflow-hidden bg-gradient-to-br from-black via-gray-800 to-black">
        {/* Complex depth of field background with multiple light sources */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40"></div>
        
        {/* Multiple radial light spots creating bokeh effect */}
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-gradient-radial from-brand-orange/30 via-brand-orange/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-80 h-80 bg-gradient-radial from-brand-purple/25 via-brand-purple/8 to-transparent rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-gradient-radial from-brand-blue/15 via-brand-blue/5 to-transparent rounded-full blur-3xl"></div>
        
        {/* Diagonal light streams */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-brand-orange/8 to-transparent transform rotate-12 scale-150"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-brand-purple/6 to-transparent transform -rotate-6 scale-125"></div>
        
        <div className="container mx-auto px-4 pt-12 relative">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-8 leading-tight text-purple-500 md:text-6xl">
              {currentContent.about.title}
            </h1>
            <p className="text-3xl text-white/70 mb-12 max-w-3xl mx-auto leading-relaxed md:text-2xl">
              {currentContent.about.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Company Stats & Facts */}
      <section className="py-10 bg-gray-50 dark:bg-gray-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
              <div className="text-center group">
                <div className="text-6xl md:text-7xl font-bold text-primary mb-4 transition-transform group-hover:scale-110">2012</div>
                <div className="text-lg text-muted-foreground font-medium">Grundades</div>
              </div>
              <div className="text-center group">
                <div className="text-6xl md:text-7xl font-bold text-primary mb-4 transition-transform group-hover:scale-110">100+</div>
                <div className="text-lg text-muted-foreground font-medium">Filmproduktioner</div>
              </div>
              <div className="text-center group">
                <div className="text-6xl md:text-7xl font-bold text-primary mb-4 transition-transform group-hover:scale-110">25+</div>
                <div className="text-lg text-muted-foreground font-medium">Multinationella kunder</div>
              </div>
            </div>
            
            {/* Volym Agency Section */}
            <div className="mb-16">
              <div className="max-w-4xl">
                <h3 className="text-3xl font-bold mb-8">Volym Digitalbyrå AB</h3>
                <p className="text-lg text-muted-foreground mb-6">Företaget startades av Johan Jarlsmark och Andreas Bladini som fortfarande driver det tillsammans.</p>
                <p className="text-lg text-muted-foreground mb-6">(Group)</p>
                <div className="space-y-6">
                  <p className="text-lg text-muted-foreground leading-relaxed"><a href="https://www.volymkommunikation.se" target="_blank" rel="noopener noreferrer"><strong className="text-primary hover:underline">Volym Agency</strong></a> - vår digitalbyrå där vi arbetat kontinuerligt med bl.a. videomarknadsföring i över 13 år. Vi har hjälpt både små och multinationella företag som t.ex. <strong>Volvo, Epiroc, Fazer, Siemens, Svff, Mechanum, Organic Response (Fagerhult Group), Nordic Water, Volvia, Svenska Hus, Varbergs Kommun, Lime Technologies</strong> och många fler.</p>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed"><a href="https://volymstudio.com" target="_blank" rel="noopener noreferrer"><strong className="text-primary hover:underline">Volym Studio</strong></a> - vår nya AI-hybridfilm-studio där vi kombinerar den senaste AI-teknologin med traditionell filmexpertis för att skapa kostnadseffektiva och innovativa videolösningar. Här dedikerar vi oss till 100 % åt att skapa högkonverterande filmer till mindre företag och stora marknadsteam.</p>
                </div>
              </div>
            </div>
            
            {/* Volym Studio Section */}
            <div className="mb-16">
              <div className="max-w-4xl">
                
                <div className="space-y-6">
                </div>
              </div>
            </div>
            
            {/* Strengths & Additional Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-16">
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <Star className="w-12 h-12 text-primary mb-4 mx-auto" />
                  <h3 className="text-3xl font-bold">Våra unika styrkor</h3>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-8 rounded-xl bg-card/50 border border-border/30 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    <Globe className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg leading-relaxed text-foreground">Vi förstår den nordiska affärskulturen och att den är annorlunda mot resten av världen. Det är viktigt att förstå att det som fungerar i t.ex. USA eller Tyskland inte alltid lämpar sig särskilt bra här hemma.</span>
                  </div>
                  <div className="flex items-start gap-4 p-8 rounded-xl bg-card/50 border border-border/30 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    <TrendingUp className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg leading-relaxed text-foreground">Genom lång erfarenhet av videomarknadsföring i Volym Agency har vi fått ovärdeliga insikter i vad som konverterar och skapar engagemang.</span>
                  </div>
                  <div className="flex items-start gap-4 p-8 rounded-xl bg-card/50 border border-border/30 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    <Award className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg leading-relaxed text-foreground">Vi var bland de första i Sverige att erbjuda explainer videos i Sverige 2012 och flera andra företag har tagit efter oss när vi utvecklat tjänsterna kontinuerligt sedan dess.</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <Target className="w-12 h-12 text-primary mb-4 mx-auto" />
                  <h3 className="text-3xl font-bold">Våra mål & värderingar</h3>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-8 rounded-xl bg-card/50 border border-border/30 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    <Rocket className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg leading-relaxed text-foreground">Målet är att leda utvecklingen inom AI-hybridfilm i Sverige.</span>
                  </div>
                  <div className="flex items-start gap-4 p-8 rounded-xl bg-card/50 border border-border/30 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    <Shield className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg leading-relaxed text-foreground">Compliance - vi förstår lagkraven på transparens och uppmärkning av AI-innehåll enligt AI act och säkerställer alltid GDPR.</span>
                  </div>
                  <div className="flex items-start gap-4 p-8 rounded-xl bg-card/50 border border-border/30 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    <Leaf className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg leading-relaxed text-foreground">Vi tar hållbarhet på allvar. Genom att använda AI-modeller ansvarsfullt minskar vi onödigt resurs­slöseri. Istället för tidskrävande resor mellan inspelningsplatser skapar vi material digitalt – smartare, effektivare och med lägre klimatavtryck.</span>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 bg-gradient-to-b from-muted/20 to-muted/40">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold mb-8">{currentContent.about.history.title}</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {currentContent.about.history.content1}
                </p>
              </div>
              <div className="relative group">
                <div className="aspect-video bg-gradient-primary rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex items-center gap-3">
                    <Leaf className="w-12 h-12 text-green-500" />
                    <Zap className="w-10 h-10 text-primary" />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="order-2 md:order-1 relative group">
                <div className="aspect-video bg-gradient-primary rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex items-center gap-3">
                    <Shield className="w-12 h-12 text-primary" />
                    <Eye className="w-10 h-10 text-green-500" />
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2 space-y-6">
                <h2 className="text-4xl font-bold mb-8">Etisk AI-användning</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Allt vårt arbete med AI bygger på tydliga principer för ansvar och transparens. Vi följer GDPR och den kommande EU AI Act för att säkerställa högsta nivå av dataskydd, rättvisa och öppenhet. Våra kunder ska alltid veta hur AI används i projekten, och vi tar fullt ansvar för att tekniken används på ett etiskt och hållbart sätt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>



      <Footer content={currentContent.footer} />
    </div>;
};
export default OmOss;